import { AuthLayout, OAuthButtons } from '@/components/auth'
import {
  Button,
  Control,
  EmailInput,
  PasswordInput,
  useForm,
  Icon,
} from '@tempots/beatui'
import { attr, html, on, Ensure, prop } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { Anchor, NavigationService } from '@tempots/ui'
import { auth, authStore } from '@/services/gateway'
import { loginSchema } from '@/models/auth'
import { isApiError } from '@/services/errors'

export function SigninPage() {
  const unverifiedEmail = prop<string>('')
  const resending = prop(false)
  const resendSuccess = prop(false)

  const { controller, submit, submitting } = useForm({
    initialValue: { email: '', password: '' },
    schema: loginSchema,
    onSubmit: async (value) => {
      try {
        // Reset unverified email state
        unverifiedEmail.set('')
        resendSuccess.set(false)

        await auth.login(value)
        // Refresh auth state to pick up the new session cookies
        await authStore.refresh()
        NavigationService.navigate('/dashboard')
        return Validation.valid
      } catch (err) {
        // Check for unverified email error
        if (
          isApiError(err) &&
          err.status === 401 &&
          err.action?.type === 'verify_email'
        ) {
          unverifiedEmail.set(err.action.email || value.email)
          return Validation.invalid({
            message:
              err.detail ?? 'Please verify your email before signing in.',
          })
        }

        const message = isApiError(err)
          ? (err.detail ?? err.title)
          : err instanceof Error
            ? err.message
            : 'An error occurred'
        return Validation.invalid({ message })
      }
    },
  })

  const handleResendVerification = async () => {
    const email = unverifiedEmail.value
    if (!email || resending.value) return

    resending.set(true)
    resendSuccess.set(false)

    try {
      await auth.resendVerification({ email })
      resendSuccess.set(true)
      // Redirect to verification sent page after 1.5 seconds
      setTimeout(() => {
        NavigationService.navigate(
          `/verify-email-sent?email=${encodeURIComponent(email)}`
        )
      }, 1500)
    } catch {
      // Reset resend success on error
      resendSuccess.set(false)
    } finally {
      resending.set(false)
    }
  }

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full space-y-8'),
      html.div(
        attr.class('text-center'),
        html.h2(
          attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
          'Sign in to Verts'
        ),
        html.p(
          attr.class('mt-2 text-sm sm:text-base text-gray-600'),
          'Welcome back! Please sign in to continue'
        )
      ),
      // Error message display
      Ensure(controller.error, (error) =>
        html.div(
          attr.class('space-y-3'),
          html.div(
            attr.class(
              'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
            ),
            error
          ),
          // Show resend verification button for unverified email
          unverifiedEmail.map((email) =>
            email
              ? html.div(
                  attr.class(
                    'bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3'
                  ),
                  html.div(
                    attr.class('flex items-start gap-3'),
                    Icon(
                      { icon: 'mdi:email-alert' },
                      attr.class('w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5')
                    ),
                    html.div(
                      attr.class('flex-1'),
                      html.p(
                        attr.class('text-sm text-yellow-700 mb-2'),
                        'Your email address has not been verified yet.'
                      ),
                      resendSuccess.map((success) =>
                        success
                          ? html.div(
                              attr.class(
                                'text-sm text-green-700 flex items-center gap-2'
                              ),
                              Icon(
                                { icon: 'mdi:check-circle' },
                                attr.class('w-4 h-4')
                              ),
                              'Verification email sent! Redirecting...'
                            )
                          : html.button(
                              attr.type('button'),
                              attr.class(
                                resending.map((r) =>
                                  r
                                    ? 'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-yellow-100 text-yellow-700 cursor-not-allowed'
                                    : 'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                )
                              ),
                              attr.disabled(resending),
                              on.click(handleResendVerification),
                              Icon(
                                { icon: 'mdi:email-send' },
                                attr.class('w-4 h-4')
                              ),
                              resending.map((r) =>
                                r ? 'Sending...' : 'Resend Verification Email'
                              )
                            )
                      )
                    )
                  )
                )
              : null
          )
        )
      ),
      html.form(
        on.submit((e) => {
          e.preventDefault()
          submit(e)
        }),
        attr.class('space-y-6'),
        html.div(
          Control(EmailInput, {
            controller: controller.field('email'),
            label: 'Email Address',
          })
        ),
        html.div(
          Control(PasswordInput, {
            controller: controller.field('password'),
            label: 'Password',
          })
        ),
        html.div(
          attr.class('flex justify-end flex-col sm:flex-row gap-3 sm:gap-0'),
          html.div(
            Anchor(
              { href: '/forgot-password' },
              attr.class(
                'text-sm font-medium text-brand-600 hover:text-brand-700'
              ),
              'Forgot password?'
            )
          )
        ),
        html.div(
          attr.class('flex items-center justify-center'),
          Button(
            {
              fullWidth: true,
              loading: submitting,
              disabled: submitting,
              type: 'submit',
            },
            submitting.map((s) => (s ? 'Signing in...' : 'Sign In'))
          )
        ),
        OAuthButtons({})
      ),
      html.p(
        attr.class('text-center text-sm text-gray-600'),
        "Don't have an account? ",
        Anchor(
          { href: '/signup' },
          attr.class('font-medium text-brand-600 hover:text-brand-700'),
          'Sign up'
        )
      )
    )
  )
}
